import format from 'date-fns/format';
import React, { useMemo, useState } from 'react';

import { ENTITY_TYPES } from 'config/constants';
import { COLORS } from 'config/constants/styles';
import routeMap from 'config/routes/routeMap';
// import { generateSelectorsFromTestIds } from 'helpers/utilities';
import useBreakpoint from 'hooks/useBreakpoint';

import DropdownMenu from 'components/DropdownMenu';
import EntityElement from 'components/EntityElement';
import { CheckmarkCircleIcon, EnvelopeIcon } from 'components/Icons';
import Table from 'components/Lists/Table';
import { TableColumn } from 'components/Lists/TableTypes';
import StarRating from 'components/StarRating';
import TablePaginationControls from 'components/TablePaginationControls/TablePaginationControls';
import { TombstoneText } from 'components/Tombstone';
import Tooltip from 'components/Tooltip/Tooltip';
import { LabelSubscription, useLabelSubscribersContext } from './LabelSubscribersContext';
import ManualShareModal from './ManualShareModal';

import { LABEL_SUBSCRIBER_ACTION_TEST_IDS } from './buildLabelSubscriberActions';
import {
  getArchiveAccessExpirationFormatted,
  labelSubscriberAverageRating
} from './helpers';
import useQueryLabelSubscribers from './useQueryLabelSubscribers';

const INITIAL_SORT_BY = [{ id: 'dateAdded', desc: true }];

interface Artist {
  id: string;
  slug: string;
  image: {
    url: string;
  };
  name: string;
  spotifyPopularity: number;
}

const LabelSubscribersTableDesktop = () => {
  const {
    page,
    perPage,
    sortBy,
    setSortBy,
    getSubscriberActions,
    isSmallScreen,
    label,
    setUrlQuery
  } = useLabelSubscribersContext();

  const [showManualShareModal, setShowManualShareModal] = useState(false);
  const isMediumScreen = useBreakpoint(
    useBreakpoint.BREAKPOINTS.MD,
    useBreakpoint.DIRECTIONS.DOWN
  );
  // custom adjustment for the spotify popularity column
  const isBigScreen = useBreakpoint(1650, useBreakpoint.DIRECTIONS.UP);

  const { data: subscribers, fetching, pageInfo } = useQueryLabelSubscribers(page);
  const displayLoading = fetching;

  const archiveAccessExists = subscribers.some(s => s.hasArchiveAccess);
  const convertedSortBy = useMemo(
    () =>
      Object.entries(sortBy).map(([id, direction]) => ({
        id,
        desc: direction === 'DESC'
      })),
    [sortBy]
  );

  // TODO: move type definition to Table component
  const columns: TableColumn[] = useMemo(
    () =>
      [
        {
          Header: 'Subscriber',
          accessor: 'node',
          canSort: true,
          id: 'artistName',
          // eslint-disable-next-line react/prop-types
          Cell: ({ value: artist, row }: { value: Artist; row: { index: number } }) => {
            return (
              <>
                {displayLoading ? (
                  <EntityElement.Tombstone
                    type={ENTITY_TYPES.ARTIST}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    size={EntityElement.SIZES.SMALL}
                    index={row.index}
                  />
                ) : (
                  <EntityElement
                    path={artist.slug}
                    imageUrl={artist.image.url}
                    title={artist.name}
                    type={ENTITY_TYPES.ARTIST}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    size={EntityElement.SIZES.SMALL}
                    // NOTE: lazy loading list images is incredibly laggy and 'debounce' experience is not pretty
                    lazy={false}
                  />
                )}
              </>
            );
          },
          minWidth: isMediumScreen ? 180 : 200
        },
        {
          Header: 'Accepted',
          id: 'accepted',
          accessor: 'isFollowing',
          headerToolTip: (
            <>
              Invites are sent according to artist preferences (Thursdays by default). To
              avoid spam, our app does NOT support automatic or in-app reminders. If you
              are in personal contact with the artist, click the envelope icon next to
              their name to get a mobile-friendly URL to directly share with them.
            </>
          ),
          Cell: ({ value: isFollowing }: { value: boolean }) => {
            if (displayLoading) {
              return <TombstoneText style={{ width: 15, marginLeft: 25 }} />;
            }

            if (isFollowing) {
              return (
                <CheckmarkCircleIcon color={COLORS.SUCCESS} style={{ paddingLeft: 25 }} />
              );
            }

            return (
              <EnvelopeIcon
                style={{ cursor: 'pointer', paddingLeft: 25 }}
                onClick={() => setShowManualShareModal(true)}
              />
            );
          },
          minWidth: 82,
          width: 90
        },
        {
          Header: 'Date Invited',
          accessor: 'addedAt',
          id: 'dateInvited',
          canSort: true,
          hide: isSmallScreen,
          sortDescFirst: true,
          Cell: ({ value: dateAdded }: { value: string }) => {
            if (displayLoading) {
              return <TombstoneText style={{ marginTop: '1rem', width: '5rem' }} />;
            }

            if (!dateAdded) return null;
            return <>{format(new Date(dateAdded), 'yyyy-MM-dd')}</>;
          },
          width: 100
        },
        archiveAccessExists && {
          Header: 'Archive Access',
          accessor: 'archiveAccessExpiresAt',
          id: 'archiveAccessExpiresAt',
          headerTestId: LabelSubscribersTableDesktop.TEST_IDS.ARCHIVE_ACCESS_HEADER,
          Cell: ({ value: expiresDate }: { value: string }) => {
            if (displayLoading || !expiresDate) return null;

            return (
              <span
                data-testid={LabelSubscribersTableDesktop.TEST_IDS.ARCHIVE_ACCESS_CELL}
              >
                {getArchiveAccessExpirationFormatted(expiresDate)}
              </span>
            );
          },
          width: 100
        },
        {
          Header: 'Spotify Popularity',
          canSort: true,
          headerToolTip: (
            <span>
              <a
                href={routeMap.external.help.spotifyPopularityIndex}
                rel="noreferrer noopener"
                target="_blank"
                onClick={e => e.stopPropagation()}
              >
                Learn more
              </a>{' '}
              about the Spotify Popularity Score.
            </span>
          ),
          headerTooltipProps: {
            interactive: true
          },
          accessor: 'node',
          id: 'artistPopularity',
          headerTestId: LabelSubscribersTableDesktop.TEST_IDS.POPULARITY_HEADER,
          sortDescFirst: true,
          Cell: ({ value: artist }: { value: Artist }) => {
            function renderPopularity() {
              if (displayLoading) {
                return <TombstoneText style={{ marginTop: '1rem', width: '2rem' }} />;
              }

              const { spotifyPopularity } = artist;
              if (!spotifyPopularity) return '-';
              return spotifyPopularity;
            }

            // TODO: ideally we'd measure the cell width to determine if we should use more or less left padding
            // This is possible with react-table v8
            return (
              <div style={{ paddingLeft: isBigScreen ? 55 : 25 }}>
                {renderPopularity()}
              </div>
            );
          },
          minWidth: 105,
          width: 120
        },
        {
          Header: 'Latest Reaction',
          accessor: 'lastReactedAt',
          canSort: true,
          hide: isSmallScreen,
          sortDescFirst: true,
          Cell: ({ value: lastReactedAt }: { value: string }) => {
            if (displayLoading) {
              return <TombstoneText style={{ marginTop: '1rem', width: '5rem' }} />;
            }

            if (!lastReactedAt) return null;
            return <>{format(new Date(lastReactedAt), 'yyyy-MM-dd')}</>;
          },
          width: 105
        },
        {
          Header: 'Avg. Rating',
          canSort: true,
          accessor: 'averageRating',
          id: 'averageRating',
          sortDescFirst: true,
          Cell: ({ value: avgRating }: { value: number }) => {
            if (displayLoading) {
              return <TombstoneText style={{ marginTop: '1rem', width: '5rem' }} />;
            }

            const displayRating = labelSubscriberAverageRating(
              avgRating,
              'No reactions from this artist'
            );

            return (
              <div style={{ width: 100 }}>
                <Tooltip
                  content={displayRating}
                  triggerContainerElement="div"
                  style={{ display: 'flex' }}
                  placement="right"
                >
                  <StarRating rating={avgRating} displayOnly grayScale />
                </Tooltip>
              </div>
            );
          },
          minWidth: 120
        },
        {
          Header: '',
          accessor: (subscription: LabelSubscription) => subscription,
          id: 'actions',
          Cell: ({ value: subscription }: { value: LabelSubscription }) => {
            if (displayLoading) return null;
            const actions = getSubscriberActions(subscription);
            return (
              <DropdownMenu
                triggerTestId={LabelSubscribersTableDesktop.TEST_IDS.MENU_TOGGLE}
                actions={actions}
              />
            );
          },
          width: 40
        }
      ].filter(Boolean),
    [
      getSubscriberActions,
      isSmallScreen,
      isBigScreen,
      isMediumScreen,
      displayLoading,
      archiveAccessExists
    ]
  );

  return (
    <>
      <Table
        style={{ overflowX: 'auto' }}
        columns={columns}
        setSortBy={setSortBy}
        sortBy={convertedSortBy}
        pagination
        // In order to display tombstones, we pass three empty items for the table component to render rows with
        data={displayLoading ? [...Array<undefined>(10)].map(() => ({})) : subscribers}
        data-testid={LabelSubscribersTableDesktop.TEST_IDS.CONTAINER}
        rowDataTestId={(row: { original: LabelSubscription }) =>
          LabelSubscribersTableDesktop.TEST_IDS.rowId(row.original.node?.id)
        }
        isLoading={displayLoading}
        initialSortBy={INITIAL_SORT_BY}
        autoResetSortBy={false}
      />

      <TablePaginationControls
        currentPage={page}
        itemsPerPage={perPage}
        nodeCount={pageInfo?.nodeCount}
        setPageCount={p => setUrlQuery({ page: p })}
        setItemsPerPage={perPage => setUrlQuery({ page: 1, perPage })}
      />

      {showManualShareModal && (
        <ManualShareModal
          labelId={label.id}
          close={() => setShowManualShareModal(false)}
        />
      )}
    </>
  );
};

// LABEL_SUBSCRIBER_ACTION_TEST_IDS;
LabelSubscribersTableDesktop.TEST_IDS = {
  CONTAINER: 'LabelSubscribers',
  MENU_TOGGLE: 'LabelSubscriberTable--row--menu-toggle',
  ...LABEL_SUBSCRIBER_ACTION_TEST_IDS,
  POPULARITY_HEADER: 'LabelSubscriberTable-popularity-header',
  ARCHIVE_ACCESS_HEADER: 'LabelSubscriberTable-archive-access-header',
  ARCHIVE_ACCESS_CELL: 'LabelSubscriberTable-archive-access-cell',
  rowId: artistId => `LabelSubscriber-row--artistId-${artistId}`
};

export default LabelSubscribersTableDesktop;
